import { useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BASE = process.env.REACT_APP_BACKEND_URL;

export function VoiceButton({ onText, className = "", testId = "voice-btn" }) {
  const [state, setState] = useState("idle");
  const rec = useRef(null);
  const chunks = useRef([]);

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia) return toast.error("Voice input isn't supported in this browser");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const r = new MediaRecorder(stream, { mimeType: mime });
      chunks.current = [];
      r.ondataavailable = (e) => { if (e.data.size) chunks.current.push(e.data); };
      r.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setState("busy");
        const blob = new Blob(chunks.current, { type: mime });
        const fd = new FormData(); fd.append("audio", blob, mime.includes("mp4") ? "clip.mp4" : "clip.webm");
        try {
          const res = await fetch(`${BASE}/api/transcribe`, { method: "POST", body: fd });
          if (!res.ok) throw new Error();
          const { text } = await res.json();
          if (text) onText(text); else toast.message("Didn't catch that — try again");
        } catch { toast.error("Couldn't transcribe — please try again"); }
        finally { setState("idle"); }
      };
      rec.current = r; r.start(); setState("rec");
    } catch { toast.error("Microphone access was blocked"); }
  };
  const stop = () => rec.current?.stop();

  const Icon = state === "rec" ? Square : state === "busy" ? Loader2 : Mic;
  return (
    <button type="button" onClick={state === "rec" ? stop : start} disabled={state === "busy"} data-testid={testId}
      title={state === "rec" ? "Stop recording" : "Speak instead of typing"} aria-label={state === "rec" ? "Stop recording" : "Speak instead of typing"}
      className={`rounded-full border px-3 py-2 min-h-[40px] text-xs font-mono uppercase tracking-widest flex items-center gap-2 transition-colors disabled:opacity-60 ${
        state === "rec" ? "bg-red-500/20 border-red-500 text-red-300 animate-pulse" : "bg-slate-900/50 border-slate-700 text-slate-300 hover:border-cyan-400"
      } ${className}`}>
      <Icon className={`w-4 h-4 ${state === "busy" ? "animate-spin" : ""}`} />
      {state === "rec" ? "Stop" : state === "busy" ? "Transcribing" : "Speak"}
    </button>
  );
}
